import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, FONT_FAMILY } from '../theme';
import Lenis from '@studio-freight/lenis';
import FireButton from '../components/FireButton';

export default function HomeLandingPage() {
    const navigate = useNavigate();
    const lenisRef = useRef<Lenis | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Check if anonymized mode is enabled
    const isAnonMode = process.env.REACT_APP_ANON_MODE === 'true' || process.env.ANON_MODE === 'true';
    console.log('Home isAnonMode:', isAnonMode, 'REACT_APP_ANON_MODE:', process.env.REACT_APP_ANON_MODE, 'ANON_MODE:', process.env.ANON_MODE);

    useEffect(() => {
        // Initialize Lenis smooth scrolling
        lenisRef.current = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // https://www.desmos.com/calculator/brs54l4xou
        });

        // RAF loop for Lenis
        function raf(time: number) {
            lenisRef.current?.raf(time);
            requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);

        // Add scroll event listener for additional features
        const handleScroll = (e: any) => {
            // You can add scroll-triggered animations here
            const scrollY = e.scroll;
            // console.log('Scroll position:', scrollY);
        };

        lenisRef.current.on('scroll', handleScroll);

        // Add resize listener for responsive behavior
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);

        // Cleanup function
        return () => {
            if (lenisRef.current) {
                lenisRef.current.off('scroll', handleScroll);
                lenisRef.current.destroy();
            }
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleDemoClick = () => {
        navigate('/app');
    };

    const scrollToSection = (sectionIndex: number) => {
        if (lenisRef.current) {
            const sectionHeight = window.innerHeight;
            lenisRef.current.scrollTo(sectionHeight * sectionIndex, {
                duration: 2,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t))
            });
        }
    };

    return (
        <div style={{ fontFamily: FONT_FAMILY, width: '100%', overflowX: 'hidden' }}>
            {/* Trillium Technologies Logo - Top Right */}
            {!isAnonMode && (
                <div style={{
                    position: 'absolute',
                    top: '30px',
                    left: '20%',
                    transform: 'translateX(-50%)',
                    zIndex: 1001,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 'auto'
                }}>
                    <img
                        src="/logos/logo-heliolab-triangle.png"
                        alt="Heliolab"
                        style={{
                            width: isMobile ? '30%' : '15%',
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                </div>
            )}

            {/* Navigation Dots - Hidden on mobile */}
            <div style={{
                position: 'fixed',
                right: isMobile ? '-50px' : '30px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                opacity: isMobile ? 0 : 1,
                pointerEvents: isMobile ? 'none' : 'auto'
            }}>
                {[0, 1, 2, 3].map((index) => (
                    <button
                        key={index}
                        onClick={() => scrollToSection(index)}
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            border: 'none',
                            background: COLORS.focusBorder,
                            cursor: 'pointer',
                            opacity: 0.6,
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.6';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title={`Go to section ${index + 1}`}
                    />
                ))}
            </div>

            {/* Hero Section with Logo and CTA */}
            <section
                style={{
                    height: '100vh',
                    width: '100%',
                    position: 'relative',
                    background: '#000000',
                    color: COLORS.text,
                    textAlign: 'center',
                    overflow: 'hidden',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                {/* Big ARCADE Logo - centered and always fully visible */}
                <img
                    src="/arcade.png"
                    alt="ARCADE Logo"
                    style={{
                        position: 'absolute',
                        top: isMobile ? '30%' : '50%',
                        left: '50%',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100vw',
                        maxHeight: '100vh',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 0,
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                    }}
                />

                {/* Bottom container for subtitle and button */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    position: 'absolute',
                    bottom: isMobile ? '200px' : '50px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '100%',
                    maxWidth: '600px',
                    zIndex: 1,
                    padding: '0 20px'
                }}>
                    {/* Subtitle */}
                    <h2 style={{
                        fontSize: isMobile ? '1.2rem' : '2rem',
                        fontWeight: '400',
                        marginBottom: '2rem',
                        opacity: 0.9,
                        maxWidth: '90vw',
                        textAlign: 'center',
                        lineHeight: 1.4,
                        padding: '0 20px',
                        wordWrap: 'break-word'
                    }}>
                        Active Region Characterization and Analysis of Dynamics and Evolution
                    </h2>

                    {/* CTA Button */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        maxWidth: '300px'
                    }}>
                        <FireButton onClick={handleDemoClick} />
                    </div>
                </div>
            </section>

            {/* Project Blurb Section */}
            <section style={{
                minHeight: '100vh',
                width: '100%',
                background: COLORS.background,
                color: COLORS.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: '1200px',
                    textAlign: 'center',
                    padding: isMobile ? '60px 20px' : '80px 40px'
                }}>
                    <h2 style={{
                        fontSize: isMobile ? '2.2rem' : '3rem',
                        fontWeight: '700',
                        marginBottom: isMobile ? '1.5rem' : '2rem',
                        color: COLORS.accent
                    }}>
                        Forecast Solar Activity with AI
                    </h2>
                    <p style={{
                        fontSize: isMobile ? '1.1rem' : '1.4rem',
                        lineHeight: '1.8',
                        maxWidth: isMobile ? '100%' : '900px',
                        margin: '0 auto',
                        opacity: 0.9
                    }}>
                        ARCADE enables early, interpretable, and high-resolution forecasts of solar activity—critical to protect satellites, power grids, and astronauts from space weather impacts.
                    </p>
                </div>
            </section>

            {/* Photo and Paragraph Section */}
            <section style={{
                minHeight: '100vh',
                width: '100%',
                background: COLORS.leftPane,
                color: COLORS.text,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: '1200px',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '40px' : '60px',
                    alignItems: 'center',
                    padding: isMobile ? '60px 20px' : '80px 40px'
                }}>
                    {/* Photo on Left/Top */}
                    <div style={{
                        textAlign: 'center',
                        flex: isMobile ? 'none' : '1',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div
                            style={{
                                display: 'inline-block',
                                // boxShadow: '0 12px 48px rgba(0,0,0,0.32)',
                                overflow: 'visible'
                            }}
                        >
                            <img
                                src="/dash.png"
                                alt="Solar magnetogram visualization"
                                style={{
                                    width: isMobile ? '90vw' : '1200px',
                                    height: isMobile ? 'auto' : 'auto',
                                    maxWidth: '100%',
                                    display: 'block',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1rem',
                            opacity: 0.8,
                            fontStyle: 'italic'
                        }}>
                            Operational Dashboard
                        </p>
                    </div>



                    {/* Paragraph on Right/Bottom */}
                    <div style={{
                        flex: isMobile ? 'none' : '1'
                    }}>
                        <h3 style={{
                            fontSize: isMobile ? '2rem' : '2.5rem',
                            fontWeight: '600',
                            marginBottom: isMobile ? '1rem' : '1.5rem',
                            color: COLORS.accent
                        }}>
                            Integrated Solar Physics
                        </h3>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.2rem',
                            lineHeight: '1.7',
                            marginBottom: isMobile ? '1rem' : '1.5rem',
                            opacity: 0.9,
                            textAlign: 'justify'
                        }}>
                            ARCADE is a forecasting system that blends physics-based modeling with deep learning to predict the Sun’s magnetic field. It solves the Surface Flux Transport equation using real solar data and a Residual Neural Network, producing interpretable, scalable outputs.

                        </p>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.2rem',
                            lineHeight: '1.7',
                            marginBottom: isMobile ? '1rem' : '1.5rem',
                            opacity: 0.9,
                            textAlign: 'justify'
                        }}>
                            The model forecasts the emergence and motion of active regions on the Sun, generating high-resolution magnetograms with uncertainty estimates. It combines learned parameters and physical laws to improve prediction accuracy and reliability.


                        </p>
                        <p style={{
                            fontSize: isMobile ? '1rem' : '1.2rem',
                            lineHeight: '1.7',
                            opacity: 0.9,
                            textAlign: 'justify'
                        }}>
                            The aim is to provide earlier, more trustworthy forecasts—bridging physics and AI to support both scientific discovery and operational resilience for satellites, power grids, and astronauts.
                        </p>
                    </div>
                </div>
            </section>

            {/* Team Section */}
            {!isAnonMode && (
                <>
                    <section style={{
                        minHeight: '100vh',
                        width: '100%',
                        background: COLORS.background,
                        color: COLORS.text,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            width: '100%',
                            maxWidth: '1200px',
                            textAlign: 'center',
                            padding: isMobile ? '60px 40px' : '80px 80px'
                        }}>
                            <h2 style={{
                                fontSize: isMobile ? '2.2rem' : '3rem',
                                fontWeight: '700',
                                marginBottom: isMobile ? '2rem' : '3rem',
                                color: COLORS.accent
                            }}>
                                Meet the Team
                            </h2>


                            <div style={{ marginBottom: isMobile ? '60px' : '80px' }}>
                                <h3 style={{
                                    fontSize: isMobile ? '1.8rem' : '2.2rem',
                                    fontWeight: '600',
                                    marginBottom: isMobile ? '2rem' : '3rem',
                                    color: COLORS.focusBorder
                                }}>
                                    Researchers
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                                    gap: isMobile ? '20px' : '30px',
                                    alignItems: 'start',
                                    padding: '0 20px'
                                }}>
                                    {[
                                        {
                                            name: 'Nina Bonaventura',
                                            linkedin: 'https://www.linkedin.com/in/nrbonaventura/',
                                            photo: '/headshots/nina_bonaventura.png'
                                        },
                                        {
                                            name: 'Nishu Karna',
                                            linkedin: 'https://www.linkedin.com/in/nishu-karna-55a436116/',
                                            photo: '/headshots/nishu_karna.png'
                                        },
                                        {
                                            name: 'Katie Keegan',
                                            linkedin: 'https://www.linkedin.com/in/katherine-keegan-6a845817a/',
                                            photo: '/headshots/katie_keegan.png'
                                        },
                                        {
                                            name: 'Plinio Guzman',
                                            linkedin: 'https://www.linkedin.com/in/plinioguzman/',
                                            photo: '/headshots/plinio_guzman.png'
                                        }
                                    ].map((member, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '15px',
                                            padding: '20px',
                                            borderRadius: '16px',
                                            background: COLORS.leftPane,
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                            transition: 'all 0.3s ease'
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-8px)';
                                                e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.25)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                                            }}>
                                            <div style={{
                                                width: isMobile ? '80px' : '120px',
                                                height: isMobile ? '80px' : '120px',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                border: `3px solid ${COLORS.focusBorder}`,
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                                            }}>
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                        // Fallback to placeholder if image fails to load
                                                        e.currentTarget.src = 'https://via.placeholder.com/120x120/4a5568/ffffff?text=' + member.name.split(' ')[0][0];
                                                    }}
                                                />
                                            </div>
                                            <h4 style={{
                                                fontSize: isMobile ? '1.1rem' : '1.3rem',
                                                fontWeight: '600',
                                                margin: '0',
                                                color: COLORS.text
                                            }}>
                                                {member.name}
                                            </h4>
                                            <a
                                                href={member.linkedin}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    background: COLORS.focusBorder,
                                                    color: COLORS.background,
                                                    textDecoration: 'none',
                                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                                    fontWeight: '600',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = COLORS.accent;
                                                    e.currentTarget.style.transform = 'scale(1.05)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = COLORS.focusBorder;
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.047-1.852-3.047-1.853 0-2.136 1.445-2.136 2.939v5.677H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                </svg>
                                                LinkedIn
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Faculty/Advisors Section */}
                            <div>
                                <h3 style={{
                                    fontSize: isMobile ? '1.8rem' : '2.2rem',
                                    fontWeight: '600',
                                    marginBottom: isMobile ? '2rem' : '3rem',
                                    color: COLORS.focusBorder
                                }}>
                                    Faculty & Advisors
                                </h3>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                                    gap: isMobile ? '20px' : '30px',
                                    alignItems: 'start',
                                    padding: '0 20px'
                                }}>
                                    {[
                                        {
                                            name: 'Spyros Kasapis',
                                            linkedin: 'https://www.linkedin.com/in/spyroskasapis/',
                                            photo: '/headshots/spyros_kasapis.png'
                                        },
                                        {
                                            name: 'Shea Hess-Webber',
                                            linkedin: 'https://www.linkedin.com/in/shea-hess-webber/',
                                            photo: '/headshots/shea_hess.png'
                                        },
                                        {
                                            name: 'Andres Muñoz-Jaramillo',
                                            linkedin: 'https://www.linkedin.com/in/amunozj/',
                                            photo: '/headshots/andres_munos.png'
                                        },
                                        {
                                            name: 'Bhibuti Jha',
                                            linkedin: 'https://www.linkedin.com/in/dr-bibhuti-kumar-jha-136a7432/',
                                            photo: '/headshots/bibhuti_jha.png'
                                        }
                                    ].map((member, index) => (
                                        <div key={index} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '15px',
                                            padding: '20px',
                                            borderRadius: '16px',
                                            background: COLORS.leftPane,
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                            transition: 'all 0.3s ease'
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-8px)';
                                                e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.25)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                                            }}>
                                            <div style={{
                                                width: isMobile ? '80px' : '120px',
                                                height: isMobile ? '80px' : '120px',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                border: `3px solid ${COLORS.focusBorder}`,
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                                            }}>
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                        // Fallback to placeholder if image fails to load
                                                        e.currentTarget.src = 'https://via.placeholder.com/120x120/4a5568/ffffff?text=' + member.name.split(' ')[0][0];
                                                    }}
                                                />
                                            </div>
                                            <h4 style={{
                                                fontSize: isMobile ? '1.1rem' : '1.3rem',
                                                fontWeight: '600',
                                                margin: '0',
                                                color: COLORS.text
                                            }}>
                                                {member.name}
                                            </h4>
                                            <a
                                                href={member.linkedin}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    background: COLORS.focusBorder,
                                                    color: COLORS.background,
                                                    textDecoration: 'none',
                                                    fontSize: isMobile ? '0.9rem' : '1rem',
                                                    fontWeight: '600',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = COLORS.accent;
                                                    e.currentTarget.style.transform = 'scale(1.05)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = COLORS.focusBorder;
                                                    e.currentTarget.style.transform = 'scale(1)';
                                                }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.047-1.852-3.047-1.853 0-2.136 1.445-2.136 2.939v5.677H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                </svg>
                                                LinkedIn
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Logos Section */}
                    <section style={{
                        minHeight: '100vh',
                        width: '100%',
                        background: COLORS.background,
                        color: COLORS.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            width: '100%',
                            maxWidth: '1200px',
                            textAlign: 'center',
                            padding: isMobile ? '60px 40px' : '80px 80px'
                        }}>
                            <h3 style={{
                                fontSize: isMobile ? '1.8rem' : '2.2rem',
                                fontWeight: '600',
                                marginBottom: isMobile ? '2rem' : '3rem',
                                color: COLORS.accent,
                                opacity: 0.9
                            }}>
                                Partnering with FDL on Heliophysics and AI
                            </h3>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                gap: isMobile ? '30px' : '50px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: isMobile ? '2rem' : '3rem'
                            }}>
                                {[
                                    {
                                        src: '/logos/logo-fdl.png',
                                        alt: 'Frontier Development Lab',
                                        name: 'FDL',
                                        url: 'https://fdl.ai'
                                    },
                                    {
                                        src: '/logos/fused-logo-text-black-bg-transparent.png',
                                        alt: 'Fused',
                                        name: 'Fused',
                                        url: 'https://fused.io/'
                                    },
                                    {
                                        src: '/logos/logo-swri.png',
                                        alt: 'Southwest Research Institute',
                                        name: 'SWRI',
                                        url: 'https://www.swri.org/'
                                    },
                                    {
                                        src: '/logos/logo-cfa.png',
                                        alt: 'Center for Astrophysics',
                                        name: 'Harvard CFA',
                                        url: 'https://cfa.harvard.edu/'
                                    },
                                    {
                                        src: '/logos/logo-princeton.png',
                                        alt: 'Princeton University',
                                        name: 'Princeton',
                                        url: 'https://www.princeton.edu/'
                                    },
                                    {
                                        src: '/logos/logo-stanford.png',
                                        alt: 'Stanford University',
                                        name: 'Stanford',
                                        url: 'https://www.stanford.edu/'
                                    },
                                    {
                                        src: '/logos/logo-ua.png',
                                        alt: 'University of Arizona',
                                        name: 'University of Arizona',
                                        url: 'https://www.arizona.edu/'
                                    },
                                    {
                                        src: '/logos/logo-emory.png',
                                        alt: 'Emory University',
                                        name: 'Emory University',
                                        url: 'https://www.emory.edu/'
                                    },

                                ].map((logo, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '15px'
                                    }}>
                                        <a
                                            href={logo.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                                        >
                                            <div style={{
                                                width: isMobile ? '80px' : '120px',
                                                height: isMobile ? '80px' : '120px',
                                                background: COLORS.background,
                                                borderRadius: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                                padding: '15px'
                                            }}>
                                                <img
                                                    src={logo.src}
                                                    alt={logo.alt}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            </div>
                                            <span style={{
                                                fontSize: isMobile ? '0.9rem' : '1.1rem',
                                                fontWeight: '600',
                                                color: COLORS.accent
                                            }}>
                                                {logo.name}
                                            </span>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </>
            )}

            {/* Footer with Accreditations */}
            {!isAnonMode && (
                <footer style={{
                    minHeight: '20vh',
                    width: '100%',
                    background: '#000000',
                    color: COLORS.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        width: '100%',
                        maxWidth: '1200px',
                        textAlign: 'center',
                        padding: isMobile ? '40px 40px 30px' : '60px 80px 40px'
                    }}>


                        <div style={{
                            borderTop: `1px solid ${COLORS.focusBorder}`,
                            paddingTop: isMobile ? '20px' : '30px',
                            opacity: 0.7
                        }}>
                            <p style={{
                                marginBottom: '10px',
                                fontSize: isMobile ? '0.9rem' : '1rem'
                            }}>
                                © Summer 2025 FDL.ai
                            </p>
                        </div>
                    </div>
                </footer>
            )}
        </div>
    );
} 